/**
 * QMS Ticket Modal
 * Slide-over modal for viewing and editing tickets
 */

import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import type { QmsTicket } from '../../types/qms.types';
import { QmsTicketForm } from './QmsTicketForm';
import { QmsComments } from '../shared/QmsComments';
import { QmsAttachments } from '../shared/QmsAttachments';
import { QmsTicketTimeline } from '../shared/QmsTicketTimeline';
import { QmsTicketLinks } from '../shared/QmsTicketLinks';
import { QmsFollowers } from '../shared/QmsFollowers';
import { usePendingFiles } from '../../hooks/usePendingFiles';

interface QmsTicketModalProps {
  ticket: QmsTicket | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
}

export function QmsTicketModal({
  ticket,
  isOpen,
  onClose,
  onSave,
  onDelete,
}: QmsTicketModalProps) {
  const { t } = useTranslation('qms');
  const pendingFilesHook = usePendingFiles();

  if (!isOpen || !ticket) return null;

  const isNew = !ticket._id;
  const isCAPA = ticket.type === 'capa';
  const isClient = ticket.type === 'client';

  // Handle close with cleanup
  const handleClose = () => {
    pendingFilesHook.clearFiles();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Slide-over panel */}
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-4xl transform transition-transform">
          <div className="flex h-full flex-col bg-white shadow-xl">
            {/* Header */}
            <div className="bg-atek-blue px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-white">
                    {isClient ? t('modal.client', 'Client Ticket') : `${isNew ? t('modal.create') : t('modal.edit')} ${isCAPA ? t('modal.capa') : t('modal.ticket')}`}
                  </h2>
                  {!isNew && ticket._id && (
                    <p className="text-sm text-atek-text-muted mt-1">
                      {t('modal.id')}: {ticket._id}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleClose}
                  className="rounded-lg p-2 text-white hover:bg-atek-blue-dark transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <QmsTicketForm
                ticket={ticket}
                onSave={onSave}
                onClose={handleClose}
                onDelete={onDelete}
                pendingFiles={isNew ? pendingFilesHook.pendingFiles : undefined}
                onAddFiles={isNew ? pendingFilesHook.addFiles : undefined}
                onRemoveFile={isNew ? pendingFilesHook.removeFile : undefined}
                onClearFiles={isNew ? pendingFilesHook.clearFiles : undefined}
                onUpdateFileStatus={isNew ? pendingFilesHook.updateFileStatus : undefined}
              />

              {/* Comments and Timeline (only for existing non-client tickets) */}
              {!isNew && !isClient && ticket._id && (
                <div className="border-t border-gray-200 bg-gray-50">
                  <div className="p-6 space-y-8">
                    {/* Comments Section */}
                    <QmsComments ticketId={ticket._id} />

                    {/* Followers Section (has its own border-t) */}
                    <QmsFollowers
                      ticketId={ticket._id}
                      followers={ticket.followers ?? []}
                      viewerIsFollower={ticket.viewerIsFollower ?? false}
                    />

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Attachments Section */}
                    <QmsAttachments ticketId={ticket._id} />

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Related Tickets Section */}
                    <QmsTicketLinks ticket={ticket} onLinksUpdated={onSave} />

                    {/* Divider */}
                    <div className="border-t border-gray-200"></div>

                    {/* Timeline Section */}
                    <QmsTicketTimeline ticketId={ticket._id} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
